import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import useHoverDelay from 'Components/hooks/use-hover-delay'
import { headerHeight } from 'Components/new-header'
import OverflowTooltip from 'Components/new/overflow-tooltip'
import { MoreButton } from 'New/search/components/title-bar'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import noopWrapper from 'lib/noop-wrapper'

interface IProps {
  className?: string
  title?: JSX.Element | string
  navList?: { link: string; label: string; icon?: string }[]
  onChange?: (id: string) => void
  onTitleClick: () => void
  onClickItem?(id: string): void
  isFixed?: boolean
}

const FloorTabTitle: React.FC<IProps> = ({
  className = '',
  title,
  navList = [],
  onChange,
  onTitleClick,
  onClickItem,
  isFixed,
}) => {
  const [active, setActive] = useState(navList[0]?.link)
  const closeRef = useRef<() => void>()

  const handleMouseOver = useCallback(
    (id) => {
      if (id === active) return
      setActive(id)
      if (closeRef.current) {
        closeRef.current()
      }
      onChange && onChange(id)
    },
    [onChange, active]
  )

  const [start, cancel] = useHoverDelay(handleMouseOver)

  // eslint-disable-next-line complexity
  const renderContent = ({
    index,
    isInMore,
    close,
    isCover,
  }: {
    index: number
    isInMore?: boolean
    close?: () => void
    isCover?: boolean
  }) => {
    if (index !== navList.length) {
      const item = navList[index]
      const isActive = active === item.link && !isInMore
      return (
        <div
          className={classNames(
            'tw-relative',
            isInMore
              ? {
                'tw-center tw-leading-22 tw-text-center tw-rounded-2 hover:tw-bg-video-light-gray tw-cursor-pointer tw-text-16 xl:tw-text-18':
                    true,
              }
              : {
                'tw-opacity-0': isCover,
                'tw-inline-flex tw-items-center': true,
                'tw-text-common-black': isActive || isFixed,
                'tw-text-18 tw-font-medium': isActive && !isFixed,
                'tw-font-semibold': isActive && isFixed,
                active: isActive,
                'tw-text-18': isFixed,
                'tw-h-32': !isFixed,
                'tw-ml-24': !isFixed && index,
                'tw-ml-40': isFixed && index,
                'tw-text-common-gray tw-text-16': !isActive && !isFixed,
              },
            isFixed && !isInMore ? headerHeight : ''
          )}
          key={index}
          onClick={() => {
            if (close) {
              close()
            }
            if (onClickItem) onClickItem(item.link)
            handleMouseOver(item.link)
          }}
          onMouseEnter={() => {
            if (close) return
            start(item.link)
          }}
          onMouseLeave={cancel}
          style={{
            width: isInMore
              ? undefined
              : item.label === 'SUV'
                ? 36
                : item.label.length * 18,
          }}
        >
          <span
            className={classNames(
              isInMore
                ? 'tw-inline-block tw-w-full tw-h-full tw-px-20 tw-py-10'
                : '',
              'tw-cursor-pointer tw-whitespace-nowrap'
            )}
          >
            {item.label}
          </span>
          <style jsx>
            {`
              .active::after {
                display: block;
                position: absolute;
                content: '';
                bottom: 0;
                left: 0;
                right: 0;
                margin: 0 auto;
                width: 30px;
                height: 4px;
                background-color: #ffcc32;
              }
              a {
                color: inherit;
              }
            `}
          </style>
        </div>
      ) as ReactNode
    } else {
      return (
        <div
          className={classNames(
            'tw-relative',
            isInMore
              ? {
                'tw-center tw-leading-22 tw-text-center tw-rounded-2 hover:tw-bg-video-light-gray tw-cursor-pointer tw-text-16 xl:tw-text-18':
                    true,
              }
              : {
                'tw-flex tw-items-center': true,
                'tw-text-18': isFixed,
                'tw-h-32': !isFixed,
                'tw-ml-24': !isFixed && index,
                'tw-ml-40': isFixed && index,
                'tw-text-common-gray tw-text-16': !isFixed,
              },
            isFixed && !isInMore ? headerHeight : ''
          )}
        >
          <a
            href="/news"
            target={'_blank'}
            className={classNames(
              isInMore
                ? 'tw-inline-block tw-w-full tw-h-full tw-px-20 tw-py-10'
                : '',
              'tw-cursor-pointer'
            )}
            onClick={() => {
              sendTeaCommEvent('clk_event', {
                event_id: 'all_news_btn',
                module_id: 'feed_module',
              })
            }}
          >
            全部资讯{' '}
            {isInMore ? null : <i className="DCD_Icon icon_triangle_right" />}
          </a>
        </div>
      ) as ReactNode
    }
  }

  return (
    <div
      className={classNames('tw-flex', className, {
        'tw-pb-12 tw-items-center': !isFixed,
        'tw-pb-0': isFixed,
      })}
    >
      {title && (
        <div
          className="tw-text-20 tw-text-common-black tw-font-semibold"
          onClick={onTitleClick}
        >
          {title}
        </div>
      )}
      {navList?.length > 0 && (
        <OverflowTooltip
          itemCount={navList.length + 1}
          activeIndex={navList.findIndex((d) => d.link === active)}
          renderMoreWrapper={(contentCount) => (content) =>
            (
              <div
                className={classNames('tw-grid tw-p-16', {
                  'tw-grid-cols-1': contentCount === 1,
                  'tw-grid-cols-2': contentCount === 2,
                  'tw-grid-cols-3': contentCount >= 3,
                })}
              >
                {content}
              </div>
            )}
          className={classNames(isFixed ? headerHeight : 'tw-h-32')}
        >
          {({ index, isInMore, close, isCover }) =>
            renderContent({ index, isInMore, close, isCover })
          }
        </OverflowTooltip>
      )}
      <style jsx>
        {`
          a {
            color: inherit;
          }
        `}
      </style>
    </div>
  )
}

export default FloorTabTitle
